import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import isEmpty from 'lodash/isEmpty';

import moment from 'moment';
import { splitMergedTimeSlot } from '../../extensions/listing-page/utils/listing.utils/splitMergedTimeSlot';
import { types as sdkTypes, createImageVariantConfig } from '../../util/sdkLoader';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import { isTransactionsTransitionInvalidTransition, storableError } from '../../util/errors';
import { transactionLineItems, transitionPrivileged } from '../../util/api';
import * as log from '../../util/log';
import {
  updatedEntities,
  denormalisedEntities,
  denormalisedResponseEntities,
} from '../../util/data';
import {
  resolveLatestProcessName,
  getProcess,
  isBookingProcess,
  isBlockBookingMasterProcess,
  TX_TRANSITION_ACTOR_CUSTOMER,
  getAllProcessNames,
} from '../../transactions/transaction';

import { addMarketplaceEntities, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchCurrentUserNotifications } from '../../ducks/user.duck';
import {
  createSubTransaction,
  resumeCreatingSubTransactions,
  resumeTransitSubTransactions,
  transitSubTransactions,
} from '../../extensions/transaction-update/utils/api';
import {
  getFailedIntervals,
  getFailedSubTransactions,
  transactionIsCreatingSubTransactions,
  transactionIsUpdatingSubTransactions,
} from '../../extensions/transaction-update/utils/transactions';
import { shouldInitiateTransitingSubTransactions } from '../../extensions/transaction-update/transactions/transactionProcessBlockBooking';
import { restoreWaitlistApi } from '../../extensions/waitlist/api';
import isWaitlistProcess from '../../extensions/waitlist/utils/isWaitlistProcess';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const REVIEW_TX_INCLUDES = ['reviews', 'reviews.author', 'reviews.subject'];
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = (timeSlotsForDate) => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/TransactionPage/SET_INITIAL_VALUES';

export const FETCH_TRANSACTION_REQUEST = 'app/TransactionPage/FETCH_TRANSACTION_REQUEST';
export const FETCH_TRANSACTION_SUCCESS = 'app/TransactionPage/FETCH_TRANSACTION_SUCCESS';
export const FETCH_TRANSACTION_ERROR = 'app/TransactionPage/FETCH_TRANSACTION_ERROR';

export const FETCH_TRANSITIONS_REQUEST = 'app/TransactionPage/FETCH_TRANSITIONS_REQUEST';
export const FETCH_TRANSITIONS_SUCCESS = 'app/TransactionPage/FETCH_TRANSITIONS_SUCCESS';
export const FETCH_TRANSITIONS_ERROR = 'app/TransactionPage/FETCH_TRANSITIONS_ERROR';

export const TRANSITION_REQUEST = 'app/TransactionPage/MARK_RECEIVED_REQUEST';
export const TRANSITION_SUCCESS = 'app/TransactionPage/TRANSITION_SUCCESS';
export const TRANSITION_ERROR = 'app/TransactionPage/TRANSITION_ERROR';

export const FETCH_MESSAGES_REQUEST = 'app/TransactionPage/FETCH_MESSAGES_REQUEST';
export const FETCH_MESSAGES_SUCCESS = 'app/TransactionPage/FETCH_MESSAGES_SUCCESS';
export const FETCH_MESSAGES_ERROR = 'app/TransactionPage/FETCH_MESSAGES_ERROR';

export const SEND_MESSAGE_REQUEST = 'app/TransactionPage/SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'app/TransactionPage/SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_ERROR = 'app/TransactionPage/SEND_MESSAGE_ERROR';

export const SEND_REVIEW_REQUEST = 'app/TransactionPage/SEND_REVIEW_REQUEST';
export const SEND_REVIEW_SUCCESS = 'app/TransactionPage/SEND_REVIEW_SUCCESS';
export const SEND_REVIEW_ERROR = 'app/TransactionPage/SEND_REVIEW_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS =
  'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/TransactionPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR =
  'app/TransactionPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/TransactionPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/TransactionPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/TransactionPage/FETCH_LINE_ITEMS_ERROR';

const CHECK_IS_RETURNING_CUSTOMER_REQUEST =
  'app/TransactionPage/CHECK_IS_RETURNING_CUSTOMER_REQUEST';
const CHECK_IS_RETURNING_CUSTOMER_SUCCESS =
  'app/TransactionPage/CHECK_IS_RETURNING_CUSTOMER_SUCCESS';
const CHECK_IS_RETURNING_CUSTOMER_ERROR = 'app/TransactionPage/CHECK_IS_RETURNING_CUSTOMER_ERROR';

// ================ Reducer ================ //

const initialState = {
  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,
  transitionInProgress: null,
  transitionError: null,
  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  messages: [],
  initialMessageFailedToTransaction: null,
  savePaymentMethodFailed: false,
  sendMessageInProgress: false,
  sendMessageError: null,
  sendReviewInProgress: false,
  sendReviewError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  fetchTransitionsInProgress: false,
  fetchTransitionsError: null,
  processTransitions: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,

  isReturningCustomer: null,
  checkIsReturningCustomerInProgress: false,
  checkIsReturningCustomerError: null,
};

// Merge entity arrays using ids, so that conflicting items in newer array (b) overwrite old values (a).
// const a = [{ id: { uuid: 1 } }, { id: { uuid: 3 } }];
// const b = [{ id: : { uuid: 2 } }, { id: : { uuid: 1 } }];
// mergeEntityArrays(a, b)
// => [{ id: { uuid: 3 } }, { id: : { uuid: 2 } }, { id: : { uuid: 1 } }]
const mergeEntityArrays = (a, b) => {
  return a.filter((aEntity) => !b.find((bEntity) => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function transactionPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case FETCH_TRANSACTION_REQUEST:
      return { ...state, fetchTransactionInProgress: true, fetchTransactionError: null };
    case FETCH_TRANSACTION_SUCCESS: {
      const transactionRef = { id: payload.data.data.id, type: 'transaction' };
      return { ...state, fetchTransactionInProgress: false, transactionRef };
    }
    case FETCH_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransactionInProgress: false, fetchTransactionError: payload };

    case FETCH_TRANSITIONS_REQUEST:
      return { ...state, fetchTransitionsInProgress: true, fetchTransitionsError: null };
    case FETCH_TRANSITIONS_SUCCESS:
      return { ...state, fetchTransitionsInProgress: false, processTransitions: payload };
    case FETCH_TRANSITIONS_ERROR:
      console.error(payload); // eslint-disable-line
      return { ...state, fetchTransitionsInProgress: false, fetchTransitionsError: payload };

    case TRANSITION_REQUEST:
      return {
        ...state,
        transitionInProgress: payload,
        transitionError: null,
      };
    case TRANSITION_SUCCESS:
      return { ...state, transitionInProgress: null };
    case TRANSITION_ERROR:
      return {
        ...state,
        transitionInProgress: null,
        transitionError: payload,
      };

    case FETCH_MESSAGES_REQUEST:
      return { ...state, fetchMessagesInProgress: true, fetchMessagesError: null };
    case FETCH_MESSAGES_SUCCESS: {
      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case FETCH_MESSAGES_ERROR:
      return { ...state, fetchMessagesInProgress: false, fetchMessagesError: payload };

    case SEND_MESSAGE_REQUEST:
      return {
        ...state,
        sendMessageInProgress: true,
        sendMessageError: null,
        initialMessageFailedToTransaction: null,
      };
    case SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false };
    case SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    case SEND_REVIEW_REQUEST:
      return { ...state, sendReviewInProgress: true, sendReviewError: null };
    case SEND_REVIEW_SUCCESS:
      return { ...state, sendReviewInProgress: false };
    case SEND_REVIEW_ERROR:
      return { ...state, sendReviewInProgress: false, sendReviewError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const { monthId } = payload;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const { dateId } = payload;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const { dateId } = payload;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case CHECK_IS_RETURNING_CUSTOMER_REQUEST:
      return {
        ...state,
        checkIsReturningCustomerInProgress: true,
        checkIsReturningCustomerError: null,
      };

    case CHECK_IS_RETURNING_CUSTOMER_SUCCESS:
      return { ...state, checkIsReturningCustomerInProgress: false, isReturningCustomer: payload };

    case CHECK_IS_RETURNING_CUSTOMER_ERROR:
      return {
        ...state,
        checkIsReturningCustomerInProgress: false,
        checkIsReturningCustomerError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const transitionInProgress = (state) => {
  return state.TransactionPage.transitionInProgress;
};

// ================ Action creators ================ //
export const setInitialValues = (initialValues) => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const fetchTransactionRequest = () => ({ type: FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = (response) => ({
  type: FETCH_TRANSACTION_SUCCESS,
  payload: response,
});
const fetchTransactionError = (e) => ({ type: FETCH_TRANSACTION_ERROR, error: true, payload: e });

const fetchTransitionsRequest = () => ({ type: FETCH_TRANSITIONS_REQUEST });
const fetchTransitionsSuccess = (response) => ({
  type: FETCH_TRANSITIONS_SUCCESS,
  payload: response,
});
const fetchTransitionsError = (e) => ({ type: FETCH_TRANSITIONS_ERROR, error: true, payload: e });

const transitionRequest = (transitionName) => ({
  type: TRANSITION_REQUEST,
  payload: transitionName,
});
const transitionSuccess = () => ({ type: TRANSITION_SUCCESS });
const transitionError = (e) => ({ type: TRANSITION_ERROR, error: true, payload: e });

const fetchMessagesRequest = () => ({ type: FETCH_MESSAGES_REQUEST });
const fetchMessagesSuccess = (messages, pagination) => ({
  type: FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination },
});
const fetchMessagesError = (e) => ({ type: FETCH_MESSAGES_ERROR, error: true, payload: e });

const sendMessageRequest = () => ({ type: SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: SEND_MESSAGE_SUCCESS });
const sendMessageError = (e) => ({ type: SEND_MESSAGE_ERROR, error: true, payload: e });

const sendReviewRequest = () => ({ type: SEND_REVIEW_REQUEST });
const sendReviewSuccess = () => ({ type: SEND_REVIEW_SUCCESS });
const sendReviewError = (e) => ({ type: SEND_REVIEW_ERROR, error: true, payload: e });

export const fetchMonthlyTimeSlotsRequest = (monthId) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = (dateId) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = (lineItems) => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = (error) => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

const checkIsReturningCustomerRequest = () => ({ type: CHECK_IS_RETURNING_CUSTOMER_REQUEST });
const checkIsReturningCustomerSuccess = (isReturningCustomer) => ({
  type: CHECK_IS_RETURNING_CUSTOMER_SUCCESS,
  payload: isReturningCustomer,
});
const checkIsReturningCustomerError = (error) => ({
  type: CHECK_IS_RETURNING_CUSTOMER_ERROR,
  payload: error,
});

// ================ Thunks ================ //

const timeSlotsRequest = (params) => (dispatch, getState, sdk) => {
  const { listing: currentListing, ...restParams } = params;

  return sdk.timeslots.query(restParams).then((response) => {
    const denormalisedResponses = denormalisedResponseEntities(response);
    return splitMergedTimeSlot({
      availabilityPlan: currentListing?.attributes?.publicData?.availabilityPlan,
      timeSlots: denormalisedResponses,
    });
  });
};

export const fetchTimeSlots =
  (listing, start, end, timeZone, options) => (dispatch, getState, sdk) => {
    const listingId = listing.id;
    const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

    // The maximum pagination page size for timeSlots is 500
    const extraParams = extraQueryParams || {
      perPage: 500,
      page: 1,
    };

    // For small time units, we fetch the data per date.
    // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
    if (useFetchTimeSlotsForDate) {
      const dateId = stringifyDateToISO8601(start, timeZone);
      const dateData = getState().ListingPage.timeSlotsForDate[dateId];
      const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
      const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
      if (hasRecentlyFetchedData) {
        return Promise.resolve(dateData?.timeSlots || []);
      }

      dispatch(fetchTimeSlotsForDateRequest(dateId));
      return dispatch(timeSlotsRequest({ listing, listingId, start, end, ...extraParams }))
        .then((timeSlots) => {
          dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
          return timeSlots;
        })
        .catch((e) => {
          dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
          return [];
        });
    }
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));
    return dispatch(timeSlotsRequest({ listing, listingId, start, end, ...extraParams }))
      .then((timeSlots) => {
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
        return timeSlots;
      })
      .catch((e) => {
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      });
  };

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;
  const { startTime, endTime } = publicData || {};

  const sessionLengthInMinutes = moment(endTime, 'HH:mm').diff(
    moment(startTime, 'HH:mm'),
    'minutes'
  );

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
        ? 'hour'
        : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];

    const bookingLengthInMinutes =
      variants.length > 0
        ? variants.reduce((min, priceVariant) => {
            return Math.min(min, priceVariant.bookingLengthInMinutes);
          }, Number.MAX_SAFE_INTEGER)
        : sessionLengthInMinutes;

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = (intervalAlign) => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 100,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Helper to fetch correct image variants for different thunk calls
const getImageVariants = (listingImageConfig) => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      // Profile images
      'variants.square-small',
      'variants.square-small2x',

      // Listing images:
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

const listingRelationship = (txResponse) => {
  return txResponse.data.data.relationships.listing.data;
};

/**
 * Injects a transaction's included provider relationship as the included
 * listing's author relationship.
 * @param {*} txResponse A SDK response from transactions.show().
 * @returns a copy of the txResponse parameter with a listing.author
 * relationship added, if provider exists in the transaction's relationships.
 */
const injectAuthorRelationship = (txResponse) => {
  const {
    included,
    data: {
      relationships: { provider },
    },
  } = txResponse.data;

  // If provider has not been included, return the response with no changes.
  if (!provider?.data?.id) {
    return txResponse;
  }

  const includedListingIdx = included.findIndex((inc) => inc.type === 'listing');

  // We will set the transaction's provider as the listing's author.
  // The full user resource we want to associate with the listing is
  // already available in the response.data.included array, so we only
  // need to add a relationship reference in the included listing resource.
  included[includedListingIdx] = {
    ...included[includedListingIdx],
    relationships: {
      ...included[includedListingIdx].relationships,
      author: {
        data: {
          id: provider?.data?.id,
          type: 'user',
        },
      },
    },
  };

  return {
    ...txResponse,
    data: {
      ...txResponse.data,
      included: [...included],
    },
  };
};

const delay = (ms) =>
  new Promise((resolve) => {
    if (typeof window !== 'undefined') {
      window.setTimeout(resolve, ms);
    }
  });

const resumeCreateOrTransitSubTransactionsMaybe = (tx) => async (dispatch, getState) => {
  try {
    const isMasterTransaction = isBlockBookingMasterProcess(tx.attributes.processName);
    const isCreatingSubTransactions = transactionIsCreatingSubTransactions(tx);
    const isUpdatingSubTransactions = transactionIsUpdatingSubTransactions(tx);
    const failedIntervals = getFailedIntervals(tx);
    const failedSubTxs = getFailedSubTransactions(tx);
    const currentUser = getState().user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;
    const { customer } = tx;
    const isCustomer = customer?.id?.uuid === currentUserId;
    const shouldNotResumeMultipleSessions =
      (tx.attributes.protectedData.sessions?.length ?? 0) > 1 ||
      isCreatingSubTransactions ||
      isUpdatingSubTransactions;
    const shouldNotResumeOrTransitBlockBooking =
      !isMasterTransaction || isCreatingSubTransactions || isUpdatingSubTransactions;

    if (!shouldNotResumeMultipleSessions && shouldNotResumeOrTransitBlockBooking) {
      return;
    }

    const { ticketOption } = tx?.attributes?.protectedData ?? {};

    const { subTransactions, isCreatingSubTxs } = tx?.attributes?.metadata ?? {};

    const needToCreateSubTransactionsTicketType =
      ticketOption === 'payAsYouGo' || ticketOption?.includes('blockBooking');

    const checkoutCodeHasFailedToCallCreateTransaction =
      typeof subTransactions === 'undefined' || typeof isCreatingSubTxs === 'undefined';

    if (needToCreateSubTransactionsTicketType && checkoutCodeHasFailedToCallCreateTransaction) {
      createSubTransaction(tx.id.uuid).catch((e) => {
        log.error(e, 'sub-transactions-create-failed', { txId: tx.id.uuid, ticketOption });
      });
      return;
    }

    if (failedIntervals.length) {
      resumeCreatingSubTransactions(tx.id.uuid).catch((e) => {
        log.error(e, 'sub-transactions-resume-create-failed', {
          txId: tx.id.uuid,
          failedIntervalCount: failedIntervals.length,
        });
      });
      return;
    }

    if (failedSubTxs.length) {
      resumeTransitSubTransactions(tx.id.uuid).catch((e) => {
        log.error(e, 'sub-transactions-resume-transit-failed', {
          txId: tx.id.uuid,
          failedSubTxCount: failedSubTxs.length,
        });
      });
      return;
    }

    if (
      !checkoutCodeHasFailedToCallCreateTransaction &&
      shouldInitiateTransitingSubTransactions(tx)
    ) {
      await delay(300);
      transitSubTransactions(tx.id.uuid).catch((e) => {
        log.error(e, 'sub-transactions-transit-failed', { txId: tx.id.uuid });
      });
    }
  } catch (e) {
    log.error(e, 'sub-transactions-self-heal-failed', { txId: tx?.id?.uuid });
  }
};

const checkIsReturningCustomer = (transaction, txRole) => async (dispatch, getState, sdk) => {
  dispatch(checkIsReturningCustomerRequest());
  try {
    const allProcessNames = getAllProcessNames();

    const [, processNames] = allProcessNames.reduce(
      (acc, processName) => {
        if (isWaitlistProcess(processName)) {
          acc[0].push(processName);
        } else {
          acc[1].push(processName);
        }
        return acc;
      },
      [[], []]
    );
    const {
      customer: {
        id: { uuid: customerId },
      },
      provider: {
        id: { uuid: providerId },
      },
    } = transaction;

    const queryParams = {
      processNames,
      only: txRole === TX_TRANSITION_ACTOR_CUSTOMER ? 'order' : 'sale',
      userId: txRole === TX_TRANSITION_ACTOR_CUSTOMER ? providerId : customerId,
      page: 1,
      perPage: 1,
    };

    const response = await sdk.transactions.query(queryParams);
    const {
      meta: { totalItems },
    } = response.data;

    dispatch(checkIsReturningCustomerSuccess(totalItems > 0));
  } catch (e) {
    dispatch(checkIsReturningCustomerError(storableError(e)));
  }
};

export const fetchTransaction = (id, txRole, config) => (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  return sdk.transactions
    .show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'listing.currentStock',
          'listing.images',
          'booking',
          'reviews',
          'reviews.author',
          'reviews.subject',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    )
    .then((response) => {
      const listingId = listingRelationship(response).id;
      const entities = updatedEntities({}, response.data);
      const listingRef = { id: listingId, type: 'listing' };
      const transactionRef = { id, type: 'transaction' };
      const denormalised = denormalisedEntities(entities, [listingRef, transactionRef]);
      const listing = denormalised[0];
      const transaction = denormalised[1];
      const processName = resolveLatestProcessName(transaction.attributes.processName);

      if (isWaitlistProcess(processName)) {
        dispatch(checkIsReturningCustomer(transaction, txRole));
      }

      try {
        const process = getProcess(processName);
        const isInquiry = process.getState(transaction) === process.states.INQUIRY;

        // Fetch time slots for transactions that are in inquired state
        const canFetchTimeslots =
          txRole === 'customer' && isBookingProcess(processName) && isInquiry;

        if (canFetchTimeslots) {
          fetchMonthlyTimeSlots(dispatch, listing);
        }
      } catch (error) {
        console.log(`transaction process (${processName}) was not recognized`);
      }

      // API does not allow fetching transaction.listing.author, so we will
      // set the relationship manually based on the transaction's provider.
      return injectAuthorRelationship(response);
    })
    .then((response) => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(fetchTransactionSuccess(response));
      return response;
    })
    .then((response) => {
      const [tx] = denormalisedResponseEntities(response);
      dispatch(resumeCreateOrTransitSubTransactionsMaybe(tx));
      return response;
    })
    .catch((e) => {
      dispatch(fetchTransactionError(storableError(e)));
      throw e;
    });
};
const refreshTx = (sdk, txId) => sdk.transactions.show({ id: txId }, { expand: true });
const refreshTransactionEntity = (sdk, txId, dispatch) => {
  delay(3000)
    .then(() => refreshTx(sdk, txId))
    .then((response) => {
      dispatch(addMarketplaceEntities(response));
      const lastTransition = response?.data?.data?.attributes?.lastTransition;
      // We'll make another attempt if mark-received-from-purchased from default-purchase process is still the latest.
      if (lastTransition === 'transition/mark-received-from-purchased') {
        return delay(8000)
          .then(() => refreshTx(sdk, txId))
          .then((response) => {
            dispatch(addMarketplaceEntities(response));
          });
      }
    })
    .catch((e) => {
      // refresh failed, but we don't act upon it.
      console.log('error', e);
    });
};

export const makeTransition = (txId, transitionName, params) => (dispatch, getState, sdk) => {
  if (transitionInProgress(getState())) {
    return Promise.reject(new Error('Transition already in progress'));
  }
  dispatch(transitionRequest(transitionName));

  return sdk.transactions
    .show({ id: txId, include: ['listing'] })
    .then((txResponse) => {
      const [tx] = denormalisedResponseEntities(txResponse);
      const listingId = tx.listing.id.uuid;
      return transitionPrivileged({
        orderData: {},
        bodyParams: { id: txId, transition: transitionName, params: { ...params, listingId } },
        queryParams: { expand: true },
      });
    })
    .then(async (response) => {
      // Fire-and-forget: the cascade must not fail the transition the user asked
      // for. Unawaited, so its rejection only reaches this catch, not the caller's.
      const tx = response?.data?.data;
      if (tx) {
        await delay(300);
        transitSubTransactions(tx.id.uuid).catch((e) => {
          log.error(e, 'sub-transactions-transit-failed', {
            txId: tx.id.uuid,
            transition: transitionName,
          });
        });
      }
      return response;
    })
    .then((response) => {
      dispatch(addMarketplaceEntities(response));
      dispatch(transitionSuccess());
      dispatch(fetchCurrentUserNotifications());

      // There could be automatic transitions after this transition
      // For example mark-received-from-purchased > auto-complete.
      // Here, we make 1-2 delayed updates for the tx entity.
      // This way "leave a review" link should show up for the customer.
      refreshTransactionEntity(sdk, txId, dispatch);

      return response;
    })
    .catch((e) => {
      dispatch(transitionError(storableError(e)));
      log.error(e, `${transitionName}-failed`, {
        txId,
        transition: transitionName,
      });
      throw e;
    });
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest());

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then((response) => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const { totalMessages } = getState().TransactionPage;

      // Original fetchMessages call succeeded
      dispatch(fetchMessagesSuccess(messages, pagination));

      // Check if totalItems has changed between fetched pagination pages
      // if totalItems has changed, fetch first page again to include new incoming messages.
      // TODO if there're more than 100 incoming messages,
      // this should loop through most recent pages instead of fetching just the first one.
      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {
            // Original fetch was enough as a response for user action,
            // this just includes new incoming messages
          })
          .catch(() => {
            // Background update, no need to to do anything atm.
          });
      }
    })
    .catch((e) => {
      dispatch(fetchMessagesError(storableError(e)));
      throw e;
    });
};

export const fetchMoreMessages = (txId, config) => (dispatch, getState, sdk) => {
  const state = getState();
  const { oldestMessagePageFetched, totalMessagePages } = state.TransactionPage;
  const hasMoreOldMessages = totalMessagePages > oldestMessagePageFetched;

  // In case there're no more old pages left we default to fetching the current cursor position
  const nextPage = hasMoreOldMessages ? oldestMessagePageFetched + 1 : oldestMessagePageFetched;

  return dispatch(fetchMessages(txId, nextPage, config));
};

const checkIfTransactionIsWaitlist = (txId) => (dispatch, getState) => {
  const ref = { id: txId, type: 'transaction' };
  const transaction = getMarketplaceEntities(getState(), [ref])[0];
  return isWaitlistProcess(transaction?.attributes?.processName);
};

export const sendMessage = (txId, message, config) => async (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  try {
    const response = await sdk.messages.send({ transactionId: txId, content: message });
    const messageId = response.data.data.id;

    // We fetch the first page again to add sent message to the page data
    // and update possible incoming messages too.
    // TODO if there're more than 100 incoming messages,
    // this should loop through most recent pages instead of fetching just the first one.
    return dispatch(fetchMessages(txId, 1, config))
      .then(() => {
        dispatch(sendMessageSuccess());

        const isWaitlist = dispatch(checkIfTransactionIsWaitlist(txId));
        if (isWaitlist) {
          // Since email notifications are still sent for deleted transactions,
          // we need to restore the waitlist entry if either party has deleted the transaction.
          // This is a background action, so we don't await it and just log any errors.
          sdk.transactions
            .show({ id: txId })
            .then((txResponse) => {
              const tx = denormalisedResponseEntities(txResponse)[0];
              const { deletedByCustomer, deletedByProvider } = tx.attributes.metadata;
              if (deletedByCustomer || deletedByProvider) {
                restoreWaitlistApi(txId.uuid);
              }
            })
            .catch((e) => {
              console.error('Failed to check transaction for restoring waitlist entry', e);
            });
        }

        return messageId;
      })
      .catch(() => dispatch(sendMessageSuccess()));
  } catch (e) {
    dispatch(sendMessageError(storableError(e)));
    // Rethrow so the page can track whether the sending failed, and
    // keep the message in the form for a retry.
    throw e;
  }
};

// If other party has already sent a review, we need to make transition to
// transitions.REVIEW_2_BY_<CUSTOMER/PROVIDER>
const sendReviewAsSecond = (txId, transition, params, dispatch, sdk, config) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then((response) => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch((e) => {
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

// If other party has not yet sent a review, we need to make transition to
// transitions.REVIEW_1_BY_<CUSTOMER/PROVIDER>
// However, the other party might have made the review after previous data synch point.
// So, error is likely to happen and then we must try another state transition
// by calling sendReviewAsSecond().
const sendReviewAsFirst = (
  txId,
  transition,
  params,
  dispatch,
  sdk,
  config,
  secondReviewTransition
) => {
  const include = REVIEW_TX_INCLUDES;

  return sdk.transactions
    .transition(
      { id: txId, transition, params },
      { expand: true, include, ...getImageVariants(config.layout.listingImage) }
    )
    .then((response) => {
      dispatch(addMarketplaceEntities(response));
      dispatch(sendReviewSuccess());
      return response;
    })
    .catch((e) => {
      // If transaction transition is invalid, lets try another endpoint.
      if (isTransactionsTransitionInvalidTransition(e)) {
        return sendReviewAsSecond(txId, secondReviewTransition, params, dispatch, sdk, config);
      }
      dispatch(sendReviewError(storableError(e)));

      // Rethrow so the page can track whether the sending failed, and
      // keep the message in the form for a retry.
      throw e;
    });
};

export const sendReview =
  (tx, transitionOptionsInfo, params, config) => (dispatch, getState, sdk) => {
    const { reviewAsFirst, reviewAsSecond, hasOtherPartyReviewedFirst } = transitionOptionsInfo;
    dispatch(sendReviewRequest());

    return hasOtherPartyReviewedFirst
      ? sendReviewAsSecond(tx?.id, reviewAsSecond, params, dispatch, sdk, config)
      : sendReviewAsFirst(tx?.id, reviewAsFirst, params, dispatch, sdk, config, reviewAsSecond);
  };

const isNonEmpty = (value) => {
  return typeof value === 'object' || Array.isArray(value) ? !isEmpty(value) : !!value;
};

export const fetchNextTransitions = (id) => (dispatch, getState, sdk) => {
  dispatch(fetchTransitionsRequest());

  return sdk.processTransitions
    .query({ transactionId: id })
    .then((res) => {
      dispatch(fetchTransitionsSuccess(res.data.data));
    })
    .catch((e) => {
      dispatch(fetchTransitionsError(storableError(e)));
    });
};

export const fetchTransactionLineItems =
  ({ orderData, listingId, isOwnListing }) =>
  (dispatch) => {
    dispatch(fetchLineItemsRequest());
    transactionLineItems({ orderData, listingId, isOwnListing })
      .then((response) => {
        const lineItems = response.data;
        dispatch(fetchLineItemsSuccess(lineItems));
      })
      .catch((e) => {
        dispatch(fetchLineItemsError(storableError(e)));
        log.error(e, 'fetching-line-items-failed', {
          listingId: listingId.uuid,
          orderData,
        });
      });
  };

// loadData is a collection of async calls that need to be made
// before page has all the info it needs to render itself
export const loadData = (params, search, config) => (dispatch, getState) => {
  const txId = new UUID(params.id);
  const state = getState().TransactionPage;
  const txRef = state.transactionRef;
  const txRole = params.transactionRole;

  // In case a transaction reference is found from a previous
  // data load -> clear the state. Otherwise keep the non-null
  // and non-empty values which may have been set from a previous page.
  const initialValues = txRef ? {} : pickBy(state, isNonEmpty);

  dispatch(setInitialValues(initialValues));

  // Sale / order (i.e. transaction entity in API)
  return Promise.allSettled([
    dispatch(fetchTransaction(txId, txRole, config)),
    dispatch(fetchMessages(txId, 1, config)),
    dispatch(fetchNextTransitions(txId)),
  ]);
};

export const isReturningCustomerSelector = (state) => state.TransactionPage.isReturningCustomer;
